import React, { useState } from 'react';
import { useApiDocsAccess } from '../contexts/ApiDocsAccessContext';
import styles from '../styles/components/ApiDocsPasswordGate.module.scss';

interface ApiDocsPasswordGateProps {
  children: React.ReactNode;
}

const ApiDocsPasswordGate: React.FC<ApiDocsPasswordGateProps> = ({ children }) => {
  const { hasAccess, authenticate, logout } = useApiDocsAccess();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate a brief loading state for better UX
    await new Promise(resolve => setTimeout(resolve, 500));

    const success = authenticate(password);
    if (!success) {
      setError('Invalid password. Please try again.');
      setPassword('');
    }

    setIsLoading(false);
  };

  if (hasAccess) {
    return (
      <div className={styles.authenticatedWrapper}>
        <div className={styles.logoutBar}>
          <span className={styles.accessInfo}>API Documentation Access Granted</span>
          <button 
            onClick={logout}
            className={styles.logoutButton}
            type="button"
          >
            Logout from API Docs
          </button>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className={styles.passwordGate}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <h1 className={styles.title}>API Documentation</h1>
          <p className={styles.subtitle}>Liquid Credit API v2</p>
        </div>

        <div className={styles.authCard}>
          <h2 className={styles.authTitle}>Access Required</h2>
          <p className={styles.authDescription}>
            Please enter the password to access the API documentation.
          </p>

          <form onSubmit={handleSubmit} className={styles.authForm}>
            <div className={styles.inputGroup}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.passwordInput}
                placeholder="Enter API docs password"
                disabled={isLoading}
                autoFocus
              />
            </div>

            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading || !password.trim()}
            >
              {isLoading ? 'Authenticating...' : 'Access Documentation'}
            </button>
          </form>

          <div className={styles.securityNotice}>
            <p>
              <strong>Note:</strong> This password is separate from your user account and allows access to technical API documentation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocsPasswordGate;
